import React, {useCallback, useEffect, useRef, useState} from 'react'
import SerialStep from './SerialStep'
import NonSerialStep from './NonSerialStep'
import CountIterationReport from './CountIterationReport'
import {Backdrop, Box, CircularProgress, Snackbar} from '@mui/material'
import {
    flushInventoryItemsQueue,
    processFailedSaveResponse,
    processRequestItem,
    processSaveReportResponse,
    ProductSample,
    Sample,
    SaveInventoryItem,
    SaveInventoryRequestItem,
    saveReport,
    submitReport
} from '../../utils'
import {getCachedProducts} from '../../productCache'
import {DeserializeItemEvent, webEvents} from '../../../common/WebEvents'
import Notifications, {Notification} from '../../../common/Notifications'
import {useNavigate} from 'react-router-dom'
import excludedSound from '../../../assets/sounds/excluded.mp3'
import Alert from '@mui/material/Alert'
import {useDispatch} from 'react-redux'
import {createError} from '../../../store/slices/error'
import StepComponent from '../../../common/StepComponent'

const BySample = () => {
    const [serialItems, setSerialItems] = useState<SaveInventoryItem[]>([])
    const [nonSerialItems, setNonSerialItems] = useState<SaveInventoryItem[]>([])
    const [isSaving, setIsSaving] = useState(false)
    const [pendingSync, setPendingSync] = useState(false)
    const [pendingRequestItems, setPendingRequestItems] = useState<SaveInventoryRequestItem[] | null>([])
    const [pendingResponse, setPendingResponse] = useState<{items: SaveInventoryItem[], isSync: boolean} | null>(null)
    const [processQueue, setProcessQueue] = useState<boolean>(false)

    const [currentStep, setCurrentStep] = useState<number>(1)
    const [productSamples, setProductSamples] = useState<ProductSample[]>([])

    const [activeIter] = useState(() => {
        const storageItem = window.sessionStorage.getItem('activeIter')
        return storageItem ? JSON.parse(storageItem) : null
    })
    const [deserializedNotifications, setDeserializedNotifications] = useState<
        Notification[]
    >([])
    const [openDialog, setOpenDialog] = useState(false)
    const navigate = useNavigate()
    const [entryErrors, setEntryErrors] = useState<string | null>(null)
    const [loading, setLoading] = useState<{[sku: string]: boolean}>({})
    const [qtyChanged, setQtyChanged] = useState(false)
    const loadingNonSerialItems = Object.values(loading).some(isLoading => isLoading)
    const [blockingSpinner] = useState(false)
    const [failedSaveReportResponse, setFailedSaveReportResponse] = useState<boolean>(false)
    const dispatch = useDispatch()
    const audioPlayer = useRef<HTMLAudioElement | null>(null)

    const processSaveReport = useCallback((requestItems: SaveInventoryRequestItem[] | null) => {
        let isSync = requestItems === null

        setIsSaving(true)
        saveReport(activeIter?.countId, requestItems)
            .then(items => setPendingResponse({items, isSync}))
            .catch(error => {
                setIsSaving(false)
                webEvents.error(error.message, {iteration: activeIter}, error.stack)
                playAudio()
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
                setFailedSaveReportResponse(true)
            })
    }, [activeIter])

    function playAudio() {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    useEffect(() => {
        if (!activeIter) {
            navigate('/inventory/counts')
        }
        processSaveReport(null)

        webEvents.mergeOptions({
            onSyncReport: () => {
                if (isSaving)
                    setPendingSync(true)
                else
                    processSaveReport(null)
            },
            onDeserializeItem: (event: DeserializeItemEvent) => {
                if (isSaving)
                    setPendingSync(true)
                else
                    processSaveReport(null)

                setDeserializedNotifications(prevNotifications => [
                    ...prevNotifications,
                    {sku: event.sku}
                ])
            }
        })
    }, [])

    useEffect(() => {
        getCachedProducts()
            .then(prods => {
                let samples: ProductSample[] = []

                for (let s of activeIter.sample as Sample[]) {
                    if (!prods || !prods[s.sku]) {
                        console.warn(`Product with SKU ${s.sku} not found in fetched products`)
                        samples.push({
                            ...s,
                            modelNumber: '',
                            bin: ''
                        })
                    } else {
                        samples.push({
                            ...s,
                            modelNumber: prods[s.sku].model ?? '',
                            bin: prods[s.sku].bin ?? ''
                        })
                    }
                }

                setProductSamples(
                    samples.sort((a, b) => {
                        // sort by bin: if no bin, sort to end
                        if (!a.bin && b.bin) return 1
                        if (a.bin && !b.bin) return -1
                        if (a.bin !== b.bin) {
                            return a.bin.localeCompare(b.bin)
                        }

                        // secondary sort by sku
                        return a.sku.localeCompare(b.sku)
                    })
                )
            })
            .catch(error => {
                webEvents.error(
                    error.message,
                    {iteration: activeIter},
                    error.stack
                )
                playAudio()
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
    }, [activeIter, activeIter.sample])

    useEffect(() => {
        if (pendingRequestItems === null || pendingRequestItems.length)
            processSaveReport(pendingRequestItems)
    }, [pendingRequestItems, processSaveReport])

    useEffect(() => {
        if (!processQueue)
            return

        let result = flushInventoryItemsQueue(serialItems, nonSerialItems)

        if (result.serials)
            setSerialItems(result.serials)
        if (result.nonSerials)
            setNonSerialItems(result.nonSerials)

        setPendingRequestItems(result.requestItems ?? [])
        setProcessQueue(false)
    }, [nonSerialItems, processQueue, serialItems])

    useEffect(() => {
        if (pendingResponse === null)
            return

        let {serials, nonSerials} = processSaveReportResponse(
            pendingResponse.items, serialItems, nonSerialItems, pendingResponse.isSync)

        if (serials)
            setSerialItems(serials)
        if (nonSerials)
            setNonSerialItems(nonSerials)

        setIsSaving(false)
        setPendingResponse(null)

        if (pendingResponse.isSync) {
            setPendingSync(false)
            setPendingRequestItems([])
        } else if (pendingSync) {
            setPendingSync(false)
            setPendingRequestItems(null)
        } else {
            setProcessQueue(true)
        }
    }, [nonSerialItems, pendingResponse, pendingSync, serialItems])

    useEffect(() => {
        if (failedSaveReportResponse) {
            const {updatedSerialItems, updatedNonSerialItems} = processFailedSaveResponse(serialItems, nonSerialItems)
            setSerialItems(updatedSerialItems)
            setNonSerialItems(updatedNonSerialItems)
            setProcessQueue(true)
        }
        return () => setFailedSaveReportResponse(false)
    }, [failedSaveReportResponse, nonSerialItems, serialItems])

    function saveRequestItem(reqItem: SaveInventoryRequestItem) {
        let result = processRequestItem(reqItem, serialItems, nonSerialItems, isSaving || pendingSync)

        if (result === null) {
            playAudio()

            if (reqItem.serial)
                setEntryErrors(`Serial Item ${reqItem.serial} already added`)
            else
                setEntryErrors(`SKU Item ${reqItem.sku} already added`)
        } else {
            if (result.serials)
                setSerialItems(result.serials)

            if (result.nonSerials)
                setNonSerialItems(result.nonSerials)

            if (result.progress && !result.progress.startsWith('q-'))
                setPendingRequestItems([reqItem])
        }
    }

    const handleStepChange = () => {
        if (currentStep === 3) {
            processSubmitReport()
        } else {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleBack = () => {
        if (currentStep === 1) return
        setCurrentStep(currentStep - 1)
    }

    function processSubmitReport() {
        submitReport(activeIter.countId)
            .then(() => {
                webEvents.info('Report submitted successfully', {
                    iteration: activeIter
                })
                window.sessionStorage.removeItem('activeIter')
            })
            .catch(error => {
                webEvents.error(
                    `Error processing report: ${error.message}`,
                    {iteration: activeIter},
                    error
                )
                playAudio()
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
    }

    const confirmSubmit = () => {
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
    }

    const handleConfirm = () => {
        processSubmitReport()
        handleClose()
        window.sessionStorage.setItem('submissionSuccess', 'true')
        navigate('/inventory/counts')
    }

    const stepComponent = (
        <>
            {currentStep === 1 && <SerialStep
                productSamples={productSamples}
                serialItems={serialItems}
                saveRequestItem={saveRequestItem}
            />}
            {currentStep === 2 && <NonSerialStep
                productSamples={productSamples}
                nonSerialItems={nonSerialItems}
                saveRequestItem={saveRequestItem}
                loading={loading}
                setLoading={setLoading}
                qtyChanged={qtyChanged}
                setQtyChanged={setQtyChanged}
            />}
            {currentStep === 3 && <CountIterationReport serialItems={serialItems} nonSerialItems={nonSerialItems} />}
        </>
    )

    const isStepDisabled = () => {
        if (pendingRequestItems && pendingRequestItems.length > 0) return true
        if (currentStep === 2) return loadingNonSerialItems || qtyChanged
        return false
    }

    const getNextButtonLabel = (step: number) => {
        if (step === 1) return 'Next: Non-serials'
        if (step === 2) return 'Review Report'
        return ''
    }

    return (
        <>
            {entryErrors && <Snackbar
                open={true}
                key={entryErrors}
                onClose={() => setEntryErrors(null)}
                autoHideDuration={3000}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Alert
                    onClose={() => setEntryErrors(null)}
                    severity="error"
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    {entryErrors}
                </Alert>
            </Snackbar>
            }
            <Box position="fixed" top={30} right={80} zIndex={50}>
                <Notifications notificationList={deserializedNotifications} />
            </Box>
            <audio ref={audioPlayer} src={excludedSound} />
            <Backdrop open={blockingSpinner} className="z-50">
                <CircularProgress color="inherit" />
            </Backdrop>

            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <StepComponent
                    stepComponent={stepComponent}
                    currentStep={currentStep}
                    handleBack={handleBack}
                    handleStepChange={handleStepChange}
                    confirmSubmit={confirmSubmit}
                    handleClose={handleClose}
                    handleConfirm={handleConfirm}
                    openDialog={openDialog}
                    dialogTitle="Confirm Submission"
                    dialogContent="You are about to submit your report for review. Please confirm if you wish to continue."
                    dialogActions={{submitLabel: 'Submit Report'}}
                    isStepDisabled={isStepDisabled}
                    getNextButtonLabel={getNextButtonLabel}
                />
            </Box>
        </>
    )
}

export default BySample
